<template>
  <div :class="$style.flexContainer">
    <div :class="$style.firstRow">
      <section :class="$style.section">
        <h4 :class="$style.sectionLabel">
          Uptime
        </h4>
        <div :class="$style.sectionValue">
          {{ uptimeValue.number }}
          <span :class="$style.sectionSubValue">
            {{ uptimeValue.unit }}
          </span>
        </div>
      </section>
      <div :class="$style.section">
        <h4 :class="$style.sectionLabel">
          Memory
        </h4>
        <div :class="$style.sectionValue">
          {{ memoryValue.number }}
          <span :class="$style.sectionSubValue">
            {{ memoryValue.unit }}
          </span>
        </div>
      </div>
    </div>
    <div :class="$style.secondRow">
      <section :class="[$style.section, $style.storageSection]">
        <h4 :class="$style.sectionLabel">
          Storage Available
        </h4>
        <div :class="$style.sectionValueAndGraphic">
          <div :class="$style.sectionValue">
            {{ storageAvailableValue.number }}
            <span :class="$style.sectionSubValue">
              {{ storageAvailableValue.unit }}
              <span
                v-if="storageAvailablePercentage"
                :class="$style.storagePercentageText"
              >
                ({{ storageAvailablePercentage }})
              </span>
            </span>
          </div>
          <div :class="$style.storageGraphic">
            <div :class="$style.storageGraphicBackground" />
            <div
              :class="$style.storageGraphicForeground"
              :style="storageGraphicForegroundStyle"
            />
            <span
              :class="$style.storageGraphicText"
              :style="storageGraphicTextStyle"
            >
              {{ storageAvailablePercentage }}
            </span>
          </div>
        </div>
      </section>
      <section :class="$style.section">
        <h4 :class="$style.sectionLabel">
          Disk Encrypted
          <TooltipIcon
            icon="info"
            orientation="left"
            text="For more information on the Encryption Status of all drives, see System Insights."
          />
        </h4>
        <div :class="$style.sectionValue">
          {{
            diskEncryptionDisplay(item.fde, isLinux, bitlockerInfo, isWindows)
          }}
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { TooltipIcon } from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import SystemInsightsApi from 'client/api/v2/SystemInsightsApi';

import SystemInsightsComponentMixin from '../mixins/SystemInsightsComponentMixin';

const storeModel = StoreModelMap[ItemTypeConsts.system];

export default {
  name: 'InsightsSummary',

  components: { TooltipIcon },
  mixins: [SystemInsightsComponentMixin],
  SystemInsightsApi,

  data: () => ({
    bitlockerInfo: undefined,
    storageAvailablePercentage: '',
    storageAvailableValue: { number: Constants.dataNotAvailable, unit: '' },
    uptimeValue: { number: Constants.dataNotAvailable, unit: '' },
  }),

  computed: {
    ...mapGetters(storeModel, {
      isWindows: 'isWindows',
      isLinux: 'isLinux',
    }),

    memoryValue() {
      return this.formatMemoryValue(this.systemInfo.physical_memory);
    },

    storageCritical() {
      return parseInt(this.storageAvailablePercentage, 10) < 10;
    },

    storageGraphicForegroundStyle() {
      const style = { width: this.storageAvailablePercentage };
      if (this.storageCritical) style.backgroundColor = '#e54852';
      return style;
    },

    storageGraphicTextStyle() {
      const style = {};
      const percentage = parseInt(this.storageAvailablePercentage, 10);

      // Set position of graphic text (and color wrt background)
      if (percentage >= 45) {
        style.color = 'white';
        style.right = `${100 - (percentage - 5)}%`;
      } else {
        style.left = `${percentage + 5}%`;
      }

      // Set color of graphic text
      if (this.storageCritical) style.color = '#e54852';

      return style;
    },
  },

  watch: {
    storageInfo() {
      this.formatStorageInfo();
    },
  },

  created() {
    if (this.isWindows) {
      this.$options.SystemInsightsApi.getSystemInsight(this.item.id, 'bitlocker_info')
        .then((resp) => {
          const encryptedDrives = get(resp, 'data', []);
          if (encryptedDrives.length) {
            const osDrive = encryptedDrives.reduce((previous, current) => {
              if (get(current, 'drive_letter', '') === 'C:') {
                return current;
              }
              return previous;
            }, undefined);
            this.bitlockerInfo = osDrive;
          }
        });
    }
    if (this.item.active) {
      this.$options.SystemInsightsApi.getSystemInsight(this.item.id, 'uptime')
        .then((resp) => {
          const seconds = parseInt(get(resp, 'data.0.total_seconds', ''), 10);
          if (!Number.isNaN(seconds)) {
            // Convert seconds into displayable text (days or hours)
            if (seconds < 3600) {
              this.uptimeValue = { number: '< 1', unit: 'hour' };
              return;
            }

            const numHours = Math.round(seconds / 3600);
            if (numHours < 24) {
              const unit = (numHours === 1) ? 'hour' : 'hours';
              this.uptimeValue = { number: numHours, unit };
              return;
            }

            const number = Math.floor(numHours / 24);
            const unit = (number === 1) ? 'day' : 'days';
            this.uptimeValue = { number, unit };
          }
        });
    } else {
      this.uptimeValue = { number: Constants.dataNotAvailable };
    }

    this.formatStorageInfo();
  },

  methods: {
    diskEncryptionDisplay(fde, isLinux, bitlockerInfo, isWindows) {
      let display;
      if (isWindows) {
        const protectionStatus = get(bitlockerInfo, 'protection_status', 0);
        if (protectionStatus === 1) {
          display = 'Encrypted';
        } else {
          const conversionStatus = get(bitlockerInfo, 'conversion_status', -1);
          const percentageEncrypted = get(bitlockerInfo, 'percentage_encrypted', 0);
          switch (conversionStatus) {
            case 0:
              display = 'Decrypted';
              break;
            case 1:
              display = 'Encrypted (Suspended)';
              break;
            case 2:
              display = `Encrypting...(${Math.max(0, percentageEncrypted)}%)`;
              break;
            case 3:
              display = `Decrypting...(${Math.max(0, 100 - percentageEncrypted)}%)`;
              break;
            case 4:
              display = `Encryption Paused (${Math.max(0, percentageEncrypted)}%)`;
              break;
            case 5:
              display = `Decryption Paused (${Math.max(0, 100 - percentageEncrypted)}%)`;
              break;
            default:
              display = 'Unknown';
              break;
          }
        }
        return display;
      }

      if (fde && fde.active) {
        display = 'On';
      } else if (isLinux && (fde === undefined || fde.active === undefined)) {
        // per reqs, Linux has it's own display value for unknown while other OS default to 'Off'.
        display = 'See Details';
      } else {
        display = 'Off';
      }

      return display;
    },
    formatStorageInfo() {
      const { number, unit, percentage } = this.formatAvailableStorageValue(this.storageInfo);

      this.storageAvailableValue = { number, unit };
      this.storageAvailablePercentage = percentage;
    },
  },
};
</script>

<style module>
@import '../css/systemInsights.css';

@value (max-width-x-large) from '~css/breakpoints.css';
@value (gradientDark4, gradientLight4) from '~css/colors.css';

.firstRow,
.secondRow {
  display: flex;
  flex: 1;
}

.secondRow { margin-top: 10px; }

.section {
  flex: 1;
  padding: 0 25px;
}

.section:nth-child(n+2) { border-left: 1px solid gradientLight4; }

@media (min-width: max-width-x-large) {
  .flexContainer { display: flex; }
  .secondRow { margin-top: 0; }
  .storageSection { border-left: 1px solid gradientLight4; }
}

/* ------- Section Content ------- */
.sectionValueAndGraphic {
  align-items: flex-end;
  display: flex;
}

.sectionValue {
  font-size: 30px;
  white-space: nowrap;
}

.sectionValueAndGraphic > .sectionValue { flex: 1; }

.sectionSubValue {
  font-size: 16px;
  margin-left: -2px;
}

.sectionLabel {
  border-bottom: 0 !important;
  color: gradientDark4 !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  margin: 1px 0 0 !important;
  text-transform: none !important;
  white-space: nowrap;
}

.storageGraphic {
  border-radius: 4px;
  display: none; /* for now, do not display; will re-visit */
  flex: 1;
  height: 22px;
  margin-left: 20px;
  overflow: hidden;
  position: relative;
  top: -10px;
}

.storageGraphicBackground {
  background-color: #ccc;
  height: 100%;
  position: absolute;
  width: 100%;
}

.storageGraphicForeground {
  background-color: var(--jcPrimary);
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  z-index: 2;
}

.storageGraphicText {
  font-size: 11px;
  font-weight: bold;
  position: absolute;
  top: 4px;
  z-index: 3;
}
</style>
